import { Box, Chip, Icon, IconButton, Stack, Switch, Typography } from "@mui/material";
import {
	interliningExtraColumnsObj,
	trimCategoryColumn,
	trimDesignerDropdowns,
	trimsMasterColumnsObj,
	trimSubCategoryDropdowns,
	zipperExtraColumnsObj,
	zipperPullerCodeDropdowns,
	trimInterliningExtraColumnsObj,
	trimCategoryDropdownsObj,
	getAllowedTrimsBrandOptions,
	TRIM_PUBLISH_STATUS,
	getTrimPublishStatusDisplay,
	trimFinalFinishedReqDropdowns,
} from "../data";
import { arrType, inputTypes, selectOptionsKeyName } from "data/globalData";
import { statusOptions } from "data/globalData";
import { PimDashboardState } from "context/PimDashboardContext";
import { AuthState } from "context/AuthContext";
import { useMemo } from "react";
import { attrValAssocObj } from "components/PimDashboard/DashboardComponents/Designer/DesignerAttributes/data";
import RenderInputField from "components/ReusableComponents/RenderInputField/RenderInputField";
import { grey } from "@mui/material/colors";
import { Info, InfoOutlined, InfoRounded } from "@mui/icons-material";
import { LightTooltip } from "components/PimDashboard/CommonComponents/CustomToolTip/CustomToolTip";
import { getColDefaultValue } from "components/PimDashboard/data";

export const TrimMainInputs = ({ data, setData, errorObj, mode = "create", disableEdit = false }) => {
	const { allAttributes } = PimDashboardState();
	const { user } = AuthState();
	const categoryOptions = useMemo(() => {
		const attr = allAttributes?.filter((attr) => attr.key === "category")?.[0];
		if (!attr || !attr[attrValAssocObj]?.length) return [];
		return attr[attrValAssocObj]?.map((row) => ({ name: row.value, key: row.value })) || [];
	}, [allAttributes]);
	const localTrimCategoryColumn = {
		...trimCategoryColumn,
		input_type: mode === "create" ? inputTypes.SELECT.key : inputTypes.AUTO_GENERATE.key,
	};
	const createTrimCategoryOptions = useMemo(
		() =>
			trimCategoryColumn?.[selectOptionsKeyName]?.list?.filter(
				(option) =>
					option?.key !== trimCategoryDropdownsObj?.ZIPPER?.key && option?.key !== trimCategoryDropdownsObj?.THREAD?.key,
			) ?? [],
		[],
	);
	const resolvedTrimCategoryColumn =
		mode === "create"
			? {
					...localTrimCategoryColumn,
					[selectOptionsKeyName]: {
						type: arrType,
						list: createTrimCategoryOptions,
					},
				}
			: localTrimCategoryColumn;
	const localBrandColumn = useMemo(() => {
		const brandColumn = {
			...trimsMasterColumnsObj.brand,
			input_type: mode === "create" ? inputTypes.SELECT.key : inputTypes.AUTO_GENERATE.key,
		};

		if (mode !== "create") return brandColumn;

		return {
			...brandColumn,
			[selectOptionsKeyName]: {
				type: arrType,
				list: getAllowedTrimsBrandOptions(user, "create"),
			},
		};
	}, [mode, user]);

	const filteredTrimSubCategoryDropdowns = data?.[trimCategoryColumn.key]
		? trimSubCategoryDropdowns.filter((item) => item[trimCategoryColumn.key] === data[trimCategoryColumn.key])
		: [];

	const isOtherCategory = data?.[trimCategoryColumn.key] === trimCategoryDropdownsObj?.OTHER?.key;

	const itemSubCategoryColumn = isOtherCategory
		? {
				...trimsMasterColumnsObj.item_sub_category,
				input_type: inputTypes.ENTER.key,
			}
		: {
				...trimsMasterColumnsObj.item_sub_category,
				input_type: inputTypes.SELECT.key,
				[selectOptionsKeyName]: {
					type: arrType,
					list: filteredTrimSubCategoryDropdowns,
				},
			};

	return (
		<Box display="flex" gap="3%" flexWrap="wrap" flexGrow={1}>
			<RenderInputField
				column={trimsMasterColumnsObj.trim_code}
				data={data}
				setData={setData}
				selectOptObjName={selectOptionsKeyName}
				width="30%"
				error={errorObj[trimsMasterColumnsObj.trim_code.key]}
				disabled={disableEdit}
			/>
			<RenderInputField
				column={localBrandColumn}
				data={data}
				setData={setData}
				selectOptObjName={selectOptionsKeyName}
				width="30%"
				error={errorObj[localBrandColumn.key]}
				disabled={disableEdit}
			/>
			<RenderInputField
				column={resolvedTrimCategoryColumn}
				data={data}
				setData={(updated) => {
					updated(data);
				}}
				onChangeCallBack={(_, __, updated) => {
					const nextCategory = updated?.[trimCategoryColumn.key];
					const prevCategory = data?.[trimCategoryColumn.key];
					if (nextCategory !== prevCategory) {
						let updatedData = {
							...updated,
						};
						if (prevCategory === trimCategoryDropdownsObj?.INTERLINING?.key)
							Object.values(interliningExtraColumnsObj).forEach((col) => {
								updatedData[col.key] = getColDefaultValue(col);
							});
						if (prevCategory === trimCategoryDropdownsObj?.ZIPPER?.key)
							Object.values(zipperExtraColumnsObj).forEach((col) => {
								updatedData[col.key] = getColDefaultValue(col);
							});
						setData({
							...updatedData,
							[trimsMasterColumnsObj.item_sub_category.key]: "",
						});
						return;
					}
					setData(updated);
				}}
				selectOptObjName={selectOptionsKeyName}
				width="30%"
				error={errorObj[trimCategoryColumn.key]}
				disabled={disableEdit}
			/>

			<RenderInputField
				column={itemSubCategoryColumn}
				data={data}
				setData={setData}
				selectOptObjName={selectOptionsKeyName}
				width="30%"
				error={errorObj[itemSubCategoryColumn.key]}
				disabled={disableEdit}
			/>

			<RenderInputField
				column={{
					...trimsMasterColumnsObj.garment_category,
					[selectOptionsKeyName]: { type: arrType, list: categoryOptions },
				}}
				data={data}
				setData={setData}
				selectOptObjName={selectOptionsKeyName}
				width="30%"
				error={errorObj[trimsMasterColumnsObj.garment_category.key]}
				disabled={disableEdit}
			/>
		</Box>
	);
};
export const TrimCommonInputs = ({ data, setData, errorObj, mode = "create", disableEdit = false }) => {
	const filteredDesignerDropdowns = data?.[trimsMasterColumnsObj.brand.key]
		? trimDesignerDropdowns.filter((item) => item[trimsMasterColumnsObj.brand.key] === data[trimsMasterColumnsObj.brand.key])
		: [];
	const filteredFinishedRequirementDropdowns = data?.[trimsMasterColumnsObj.brand.key]
		? trimFinalFinishedReqDropdowns.filter((item) => item[trimCategoryColumn.key] === data[trimCategoryColumn.key])
		: trimFinalFinishedReqDropdowns;

	const localDesignerColumn = {
		...trimsMasterColumnsObj.designer,
		[selectOptionsKeyName]: {
			type: arrType,
			list: filteredDesignerDropdowns,
		},
	};
	const localFinishedRequirementColumn = {
		...trimsMasterColumnsObj.final_finished_requirement,
		input_type: inputTypes.SELECT.key,
		[selectOptionsKeyName]: {
			type: arrType,
			list: filteredFinishedRequirementDropdowns,
		},
	};
	return (
		<Box display="flex" gap="3%" flexWrap="wrap" flexGrow={1}>
			<Typography variant="h6" sx={{ width: "100%", p: 1, fontWeight: 400, backgroundColor: grey[50], borderRadius: 2 }}>
				Item Description:
				<span style={{ fontWeight: 500 }}>
					{data?.[trimsMasterColumnsObj.item_description.key] ? `${data[trimsMasterColumnsObj.item_description.key]} ` : ""}
				</span>
			</Typography>
			<RenderInputField
				column={localDesignerColumn}
				data={data}
				setData={setData}
				selectOptObjName={selectOptionsKeyName}
				width="30%"
				error={errorObj[trimsMasterColumnsObj.designer.key]}
				disabled={disableEdit}
			/>
			<RenderInputField
				column={trimsMasterColumnsObj.size}
				data={data}
				setData={setData}
				width="30%"
				error={errorObj[trimsMasterColumnsObj.size.key]}
				disabled={disableEdit}
			/>
			<RenderInputField
				column={trimsMasterColumnsObj.color}
				data={data}
				setData={setData}
				width="30%"
				error={errorObj[trimsMasterColumnsObj.color.key]}
				disabled={disableEdit}
			/>
			<RenderInputField
				column={localFinishedRequirementColumn}
				data={data}
				setData={setData}
				width="30%"
				selectOptObjName={selectOptionsKeyName}
				error={errorObj[trimsMasterColumnsObj.final_finished_requirement.key]}
				disabled={disableEdit}
			/>
			<RenderInputField
				column={trimsMasterColumnsObj.shade}
				data={data}
				setData={setData}
				width="30%"
				error={errorObj[trimsMasterColumnsObj.shade.key]}
				disabled={disableEdit}
			/>
		</Box>
	);
};
export const TrimZipperExtraInputs = ({ data, setData, errorObj, mode = "create", disableEdit = false }) => {
	const localPullerSizeColumn = useMemo(() => {
		const pullerCode = zipperPullerCodeDropdowns.find((item) => item.key === data[zipperExtraColumnsObj.zipper_puller_code.key]);
		const sizeDropdowns = pullerCode?.dropdowns || [];
		if (!sizeDropdowns || !pullerCode) return zipperExtraColumnsObj.zipper_puller_size;

		return {
			...zipperExtraColumnsObj.zipper_puller_size,
			[selectOptionsKeyName]: {
				type: arrType,
				list: sizeDropdowns,
			},
		};
	}, [data?.[zipperExtraColumnsObj.zipper_puller_code.key]]);

	return (
		<Box display="flex" gap="3%" flexWrap="wrap" flexGrow={1}>
			<Typography variant="h6" sx={{ width: "100%", fontWeight: 400 }}>
				Zipper Details:
			</Typography>
			<RenderInputField
				column={zipperExtraColumnsObj.zipper_type}
				data={data}
				setData={setData}
				selectOptObjName={selectOptionsKeyName}
				width="30%"
				error={errorObj[zipperExtraColumnsObj.zipper_type.key]}
				disabled={disableEdit}
			/>
			<RenderInputField
				column={zipperExtraColumnsObj.zipper_open_close}
				data={data}
				setData={setData}
				selectOptObjName={selectOptionsKeyName}
				width="30%"
				error={errorObj[zipperExtraColumnsObj.zipper_open_close.key]}
				disabled={disableEdit}
			/>
			<RenderInputField
				column={zipperExtraColumnsObj.zipper_puller_code}
				data={data}
				setData={(updated) => {
					updated(data);
				}}
				onChangeCallBack={(_, __, updated) => {
					const nextCategory = updated?.[zipperExtraColumnsObj.zipper_puller_code.key];
					const prevCategory = data?.[zipperExtraColumnsObj.zipper_puller_code.key];
					if (nextCategory !== prevCategory) {
						setData({
							...updated,
							[zipperExtraColumnsObj.zipper_puller_size.key]: "",
						});
						return;
					}
					setData(updated);
				}}
				selectOptObjName={selectOptionsKeyName}
				width="30%"
				error={errorObj[zipperExtraColumnsObj.zipper_puller_code.key]}
				disabled={disableEdit}
			/>
			<RenderInputField
				column={localPullerSizeColumn}
				data={data}
				setData={setData}
				selectOptObjName={selectOptionsKeyName}
				width="30%"
				error={errorObj[zipperExtraColumnsObj.zipper_puller_size.key]}
				disabled={disableEdit}
			/>
			<RenderInputField
				column={zipperExtraColumnsObj.zipper_puller_finish}
				data={data}
				setData={setData}
				width="30%"
				error={errorObj[zipperExtraColumnsObj.zipper_puller_finish.key]}
				disabled={disableEdit}
			/>
		</Box>
	);
};
export const TrimInterliningExtraInputs = ({ data, setData, errorObj, mode = "create", disableEdit = false }) => {
	return (
		<Box display="flex" gap="3%" flexWrap="wrap" flexGrow={1}>
			<Typography variant="h6" sx={{ width: "100%", fontWeight: 400 }}>
				Interlining Details:
			</Typography>
			{Object.values(interliningExtraColumnsObj).map((column) => (
				<RenderInputField
					key={column.key}
					column={{ ...column, name: column.name.replace("Interlining ", "") }}
					data={data}
					setData={setData}
					width="30%"
					error={errorObj[column.key]}
					disabled={disableEdit}
				/>
			))}
		</Box>
	);
};

export const TrimLifecycleActions = ({ data, setData, disableEdit = false }) => {
	const statusValue = data?.[trimsMasterColumnsObj.status.key] || statusOptions.CREATED.key;
	const activeValue = data?.[trimsMasterColumnsObj.trim_active_status.key] || "INACTIVE";
	const publishedBy = data?.[trimsMasterColumnsObj.published_by.key] || "----";
	const publishedDate = data?.[trimsMasterColumnsObj.published_date.key] || "----";
	const isPublished = statusValue === TRIM_PUBLISH_STATUS.PUBLISHED;
	const isUnpublished = statusValue === TRIM_PUBLISH_STATUS.PENDING;
	const statusDisplay = getTrimPublishStatusDisplay(statusValue);
	const showPublishMeta = isPublished || (isUnpublished && publishedBy !== "----");

	const updateActiveStatus = (nextStatus) => {
		setData((prev) => ({
			...prev,
			[trimsMasterColumnsObj.trim_active_status.key]: nextStatus,
		}));
	};

	return (
		<Box
			sx={{
				width: "100%",
				border: `1px solid ${grey[200]}`,
				borderRadius: 3,
				px: 1.5,
				py: 1.25,
				mt: 1,
				bgcolor: "#fcfcfd",
				boxShadow: "0 1px 0 rgba(15, 23, 42, 0.02)",
				boxSizing: "border-box",
			}}>
			<Stack spacing={1.25}>
				<Box
					sx={{
						display: "flex",
						alignItems: "flex-start",
						justifyContent: "space-between",
						gap: 1,
						flexWrap: "wrap",
					}}>
					<Box display="flex" columnGap={1} alignItems="center">
						<Typography fontWeight={800} sx={{ lineHeight: 1.2 }}>
							Publish Status:
						</Typography>
						<Chip
							size="small"
							label={statusDisplay.label}
							sx={{
								fontWeight: 700,
								letterSpacing: "0.04em",
								color: statusDisplay.color,
								bgcolor: statusDisplay.bgcolor,
							}}
						/>
					</Box>
				</Box>

				{showPublishMeta && (
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
							gap: 1,
						}}>
						<Box sx={{ p: 1, borderRadius: 2, bgcolor: grey[50], border: `1px solid ${grey[100]}` }}>
							<Typography variant="caption" color={grey[600]} sx={{ display: "block", mb: 0.25 }}>
								{isUnpublished ? "Unpublished By" : "Published By"}
							</Typography>
							<Typography variant="body2" fontWeight={700}>
								{publishedBy}
							</Typography>
						</Box>
						<Box sx={{ p: 1, borderRadius: 2, bgcolor: grey[50], border: `1px solid ${grey[100]}` }}>
							<Typography variant="caption" color={grey[600]} sx={{ display: "block", mb: 0.25 }}>
								{isUnpublished ? "Unpublished Date" : "Published Date"}
							</Typography>
							<Typography variant="body2" fontWeight={700}>
								{publishedDate}
							</Typography>
						</Box>
					</Box>
				)}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						gap: 1,
						flexWrap: "wrap",
						p: 1,
						borderRadius: 2,
						bgcolor: activeValue === "ACTIVE" ? "#eef8f1" : "#fafafa",
						border: `1px solid ${activeValue === "ACTIVE" ? "#cfead6" : grey[200]}`,
					}}>
					<Box display="flex" alignItems="center" columnGap={1}>
						<Typography variant="body2" fontWeight={700}>
							Active Status
						</Typography>
						<LightTooltip title="Active trims can be selected in styles and related modules. Active status can be changed anytime.">
							<IconButton size="small" color="primary">
								<InfoOutlined fontSize="small" />
							</IconButton>
						</LightTooltip>
					</Box>
					<Box display="flex" alignItems="center" gap={1}>
						<Chip
							size="small"
							label={activeValue}
							color={activeValue === "ACTIVE" ? "success" : "default"}
							sx={{ fontWeight: 700 }}
						/>
						<Switch
							checked={activeValue === "ACTIVE"}
							onChange={(e) => updateActiveStatus(e.target.checked ? "ACTIVE" : "INACTIVE")}
							color="success"
							disabled={disableEdit}
						/>
					</Box>
				</Box>
			</Stack>
		</Box>
	);
};
